import React, { useEffect, useState, useCallback } from 'react';
import { Trash2, Download, Loader2, MailOpen, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { api, BACKEND_URL } from '../lib/api';

export default function Submissions() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/submissions').then((res) => setSubs(res.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    await api.put(`/admin/submissions/${id}/read`).catch(() => {});
    setSubs(subs.map((s) => (s.id === id ? { ...s, read: true } : s)));
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this submission?')) return;
    try {
      await api.delete(`/admin/submissions/${id}`);
      setSubs(subs.filter((s) => s.id !== id));
      toast.success('Submission deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem('intexso_admin_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/submissions-export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'intexso-submissions.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="admin-submissions">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#26247B]">Form Submissions</h1>
          <p className="mt-1 text-sm text-black/60">
            Enquiries submitted through the website forms. Forward to <strong>oms.advertising@gmail.com</strong> or export as CSV.
          </p>
        </div>
        <button
          onClick={exportCsv}
          data-testid="admin-submissions-export-button"
          className="inline-flex items-center gap-2 rounded-lg bg-[#26247B] text-white px-5 py-2.5 text-sm font-bold transition-colors duration-200 hover:bg-[#1f1d66]"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <div className="mt-6 space-y-3">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#26247B]" /></div>
        ) : subs.length === 0 ? (
          <div className="bg-white border border-[#E6E8EE] rounded-2xl py-14 text-center text-sm text-black/60">No submissions yet.</div>
        ) : (
          subs.map((s) => (
            <div key={s.id} data-testid="admin-submission-row" className={`bg-white border rounded-2xl overflow-hidden ${s.read ? 'border-[#E6E8EE]' : 'border-[#F4A300]'}`}>
              <button
                className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left"
                onClick={() => {
                  setExpanded(expanded === s.id ? null : s.id);
                  if (!s.read) markRead(s.id);
                }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {s.read ? <MailOpen className="h-5 w-5 text-black/40 shrink-0" /> : <Mail className="h-5 w-5 text-[#F4A300] shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-[#26247B] truncate">
                      {s.name} <span className="font-medium text-black/60">• {s.email}</span>
                    </p>
                    <p className="text-xs text-black/60 truncate">
                      {s.product && `Products for: ${s.product} • `}
                      {s.industry && `Industry: ${s.industry} • `}
                      {s.subject && `${s.subject} • `}
                      {fmt(s.created_at)} • via {s.source}
                    </p>
                  </div>
                </div>
                <Trash2
                  className="h-4 w-4 text-red-500 hover:text-red-700 shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(s.id);
                  }}
                  data-testid="admin-submission-delete-button"
                />
              </button>
              {expanded === s.id && (
                <div className="px-5 pb-5 border-t border-[#E6E8EE] pt-4 text-sm space-y-2">
                  {s.mobile && <p><strong>Mobile:</strong> {s.mobile}</p>}
                  {s.company && <p><strong>Company:</strong> {s.company}</p>}
                  <p className="whitespace-pre-wrap leading-6 text-black/85"><strong>Message:</strong> {s.message}</p>
                  <a
                    href={`mailto:oms.advertising@gmail.com?subject=${encodeURIComponent(`Website Enquiry from ${s.name}`)}&body=${encodeURIComponent(`Name: ${s.name}\nEmail: ${s.email}\nMobile: ${s.mobile || '-'}\nCompany: ${s.company || '-'}\nProduct/Industry: ${s.product || s.industry || '-'}\nSubject: ${s.subject || '-'}\n\nMessage:\n${s.message}`)}`}
                    className="inline-flex text-[#2E8B57] font-bold hover:underline"
                  >
                    Forward to oms.advertising@gmail.com →
                  </a>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
